import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { loginPin, loginStaffPin, getLoginOptions, errText, DEMO_MODE } from '../services/api';
import { hashPin, isPlainPin } from '../utils/crypto';
import { T } from '../styles/ds';

// PH-47 / ADR-010 — the legacy shared-role login is refused with this code once ANOTHER device
// has already run the forced staff-setup cutover for this shop. `err.response.data.code` is
// preserved by api.js's interceptor specifically so this check survives the generic
// detail-to-string flattening it also does.
const isStaffLoginRequired = (err) => err?.response?.data?.code === 'staff_login_required';

// PH-47 / ADR-010 — the backend's job vocabulary (admin/sales/store_keeper/agent, see
// app/models/staff.py) isn't quite the frontend's role vocabulary (admin/sales/counter/agent,
// see App.jsx's NAV_ITEMS/ROLES) — 'store_keeper' is 'counter' here, same as `sub_for_jobs`'s own
// backend-side compatibility mapping. Used to translate the full `jobs` union PH-47's login
// returns into role strings App.jsx's nav/route filtering already understands.
const JOB_TO_FRONTEND_ROLE = { admin: 'admin', sales: 'sales', store_keeper: 'counter', agent: 'agent' };
const mapJobsToRoles = (jobs) => (Array.isArray(jobs) ? jobs.map(j => JOB_TO_FRONTEND_ROLE[j] || j) : null);

export const ROLES = {
  admin: {
    id: 'admin',
    label: 'व्यवस्थापक',
    sublabel: 'Admin — Full Access',
    description: 'सभी पेज / All pages',
    color: T.ink,
    bg: T.blue.bg,
    icon: '👑',
    pin: '1234',
  },
  sales: {
    id: 'sales',
    label: 'बिक्री कर्मचारी',
    sublabel: 'Sales Staff',
    description: 'बिक्री + उधार / Sales & Credit',
    color: T.green.text,
    bg: T.green.bg,
    icon: '🧾',
    pin: '5678',
  },
  counter: {
    id: 'counter',
    label: 'भंडार प्रभारी',
    sublabel: 'Store Keeper',
    description: 'माल + स्टॉक / Stock',
    color: T.orange.text,
    bg: T.orange.bg,
    icon: '📦',
    pin: '9999',
  },
};

// Collection agent — a DYNAMIC role: instead of a fixed shared PIN, the agent TYPES their own name
// (there is no public roster to enumerate) and enters their own per-agent PIN. Added to the login
// only for wholesale tenants (see App.buildRoles). No `pin` field — auth is per-agent, server-side.
export const AGENT_ROLE = {
  id: 'agent',
  label: 'वसूली एजेंट',
  sublabel: 'Collection Agent',
  description: 'फील्ड वसूली / Field collection',
  color: '#6a1b9a',
  bg: '#f3e5f5',
  icon: '💼',
  dynamic: true,
};

// PH-47 / ADR-010 — once a shop has run the forced staff-setup cutover, the login screen is a
// name dropdown + PIN, not role cards (decisions 1, 2, 4). Kept as a separate component so the
// legacy role-card flow below (still used pre-cutover, and during the 7-day admin grace window)
// is completely untouched when this branch never renders.
function StaffLogin({ onLogin, shopName, onForgotPin, onAccountSignIn, moduleWholesale }) {
  const [options, setOptions] = useState(null);       // [{id, name}] once loaded
  const [optionsError, setOptionsError] = useState('');
  const [staffId, setStaffId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [staffName, setStaffName] = useState('');      // shown on the mode-choice / warehouse screens
  const [needsMode, setNeedsMode] = useState(false);
  const [warehouses, setWarehouses] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  // PH-47 / ADR-010 — a PURE Collection Agent (Agent job only, no business job) is deliberately
  // excluded from GET /auth/login-options (decision 7's privacy rule — collector names stay off
  // the public pre-login list), so they can never pick a name from the dropdown above. The
  // backend's own /auth/login role="agent" path was never gated by staff_login_enabled at all
  // (login()'s dispatch reaches _login_agent before that check even runs) — the gap this closes is
  // purely that this screen offered no way to REACH it once the old role-card screen (which had
  // the typed-name Agent card) stopped rendering (Codex PR review finding, real: this was a
  // genuine "collector locked out entirely" regression, not a design choice).
  const [showAgentLogin, setShowAgentLogin] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentPin, setAgentPin] = useState('');
  const [agentError, setAgentError] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);

  const loadOptions = () => {
    setOptions(null); setOptionsError('');
    getLoginOptions()
      .then(res => setOptions(res.data?.people || []))
      .catch(err => setOptionsError(errText(err, 'सूची लोड नहीं हुई / Could not load the staff list')));
  };
  useEffect(loadOptions, []);

  const finishLogin = (res) => {
    const wid = res.data.warehouse_id ?? null;
    const wname = res.data.warehouse_name || res.data.agent_name || null;
    // PH-47 / ADR-010 — the union of every job this person holds, not just the single
    // (Admin-outranks-Sales-outranks-StoreKeeper) `role` string login returns for legacy
    // sub=="admin"-shaped backend checks. Without this, a Sales+Store Keeper person only ever got
    // `role='sales'` and App.jsx's nav/routes — filtered by that ONE string — hid Stock Count
    // entirely, despite the backend correctly authorizing it via the full jobs claim
    // (Codex PR review finding, real).
    const jobRoles = mapJobsToRoles(res.data.jobs);
    flushSync(() => { setPin(''); setError(''); setNeedsMode(false); setWarehouses(null); });
    localStorage.setItem('session', JSON.stringify({
      role: res.data.role, warehouse_id: wid, warehouse_name: wname,
      staff_id: res.data.staff_id ?? null, staff_name: res.data.staff_name ?? null,
      jobs: jobRoles, expiry: Date.now() + 8 * 60 * 60 * 1000,
    }));
    onLogin(res.data.role, wid, wname, jobRoles);
  };

  const submitAgentLogin = async (e) => {
    e.preventDefault();
    if (!agentName.trim() || !agentPin) return;
    setAgentLoading(true); setAgentError('');
    try {
      const res = await loginPin('agent', agentPin, null, agentName.trim());
      finishLogin(res);
    } catch (err) {
      setAgentError(errText(err, 'गलत नाम या PIN / Incorrect name or PIN'));
      setAgentPin('');
    } finally {
      setAgentLoading(false);
    }
  };

  const attemptLogin = async (mode) => {
    setLoading(true);
    try {
      const res = await loginStaffPin(Number(staffId), pin, null, mode || null);
      if (res.data.needs_mode) {
        setStaffName(res.data.staff_name || '');
        setNeedsMode(true);
        setLoading(false);
        return;
      }
      if (res.data.needs_warehouse) {
        setStaffName(res.data.staff_name || '');
        setWarehouses(res.data.warehouses);
        setPendingMode(mode || null);
        setLoading(false);
        return;
      }
      finishLogin(res);
    } catch (err) {
      setError(errText(err, 'गलत PIN है / Incorrect PIN'));
      setPin('');
      setNeedsMode(false);
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); if (staffId && pin) attemptLogin(null); };

  const chooseMode = (mode) => attemptLogin(mode);

  const handleWarehouseSelect = async (warehouseId) => {
    setLoading(true);
    try {
      const res = await loginStaffPin(Number(staffId), pin, warehouseId, pendingMode);
      finishLogin(res);
    } catch (err) {
      setError(errText(err, 'Login failed. Please try again.'));
      setWarehouses(null);
      setPin('');
      setLoading(false);
    }
  };

  const backToStart = () => {
    flushSync(() => { setPin(''); setError(''); setNeedsMode(false); setWarehouses(null); setPendingMode(null); });
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>{shopName || 'हमारी दुकान'}</div>
        <div style={{ fontSize: 13, color: T.sec, marginTop: 4 }}>अपना नाम चुनें — Select your name</div>
        {onAccountSignIn && (
          <button type="button" onClick={onAccountSignIn}
            style={{ background: 'none', border: 'none', color: T.blue.text, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', marginTop: 10 }}>
            मालिक? खाते से साइन इन करें / Owner? Sign in with your account →
          </button>
        )}
      </div>
      {children}
    </div>
  );

  // ── warehouse picker (several allowed stores, none picked yet) ──────────────
  if (warehouses) {
    return shell(
      <>
        <div style={{ fontSize: 13, color: T.sec, marginBottom: 12 }}>दुकान चुनें — Select your store</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 600 }}>
          {warehouses.map(w => (
            <div key={w.id} role="button" tabIndex={0} onClick={() => handleWarehouseSelect(w.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleWarehouseSelect(w.id); }}
              style={{ background: '#fff', border: `2px solid ${T.ink}`, borderRadius: 14, padding: '20px 32px', cursor: 'pointer', textAlign: 'center', minWidth: 160, boxShadow: '0 2px 8px rgba(30,58,95,0.12)' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🏪</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{w.warehouse_name}</div>
              <div style={{ fontSize: 12, color: T.mute, marginTop: 4 }}>{w.warehouse_code}</div>
            </div>
          ))}
        </div>
        {error && <div role="alert" style={{ color: T.red.text, fontSize: 13, marginTop: 16 }}>{error}</div>}
        <button onClick={backToStart} style={{ marginTop: 20, background: 'none', border: 'none', color: T.mute, fontSize: 13, cursor: 'pointer' }}>
          ← वापस जाएँ / Back
        </button>
      </>
    );
  }

  // ── counter / field mode choice (ADR-010 decision 7) ─────────────────────────
  if (needsMode) {
    return shell(
      <div style={{ background: '#fff', borderRadius: 14, padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: 340, textAlign: 'center' }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: T.ink, marginBottom: 4 }}>{staffName}</div>
        <div style={{ fontSize: 13, color: T.sec, marginBottom: 18 }}>आज किस काम के लिए लॉग इन कर रहे हैं? / What are you logging in to do today?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button type="button" disabled={loading} onClick={() => chooseMode('counter')}
            style={{ padding: '14px', background: T.ink, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            🧾 काउंटर / Counter
          </button>
          <button type="button" disabled={loading} onClick={() => chooseMode('field')}
            style={{ padding: '14px', background: T.blue.bg, color: T.ink, border: `2px solid ${T.ink}`, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            💼 फील्ड वसूली / Field collection
          </button>
        </div>
        {error && <div role="alert" style={{ color: T.red.text, fontSize: 13, marginTop: 14 }}>{error}</div>}
        <button onClick={backToStart} style={{ marginTop: 16, background: 'none', border: 'none', color: T.mute, fontSize: 13, cursor: 'pointer' }}>
          ← वापस जाएँ / Back
        </button>
      </div>
    );
  }

  // ── collection agent (typed name, off the public roster — ADR-010 decision 7) ────────────────
  // A separate screen, not a section stacked below the name+PIN form above: the two forms'
  // otherwise-identical "Enter →" submit buttons would be genuinely ambiguous to a screen reader
  // (and to an automated test) if both were visible and interactive at once.
  if (showAgentLogin) {
    return shell(
      <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: 340 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.mute, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          कलेक्शन एजेंट लॉगिन / Collection agent login
        </div>
        <form onSubmit={submitAgentLogin}>
          <input
            type="text" value={agentName} maxLength={50} autoComplete="off"
            onChange={e => { setAgentName(e.target.value); setAgentError(''); }}
            placeholder="आपका नाम / Your name" aria-label="एजेंट का नाम / Agent name"
            style={{ width: '100%', padding: '13px', border: `2px solid ${T.border}`, borderRadius: 8, fontSize: 15, marginBottom: 10, boxSizing: 'border-box' }}
          />
          <input
            type="password" inputMode="numeric" maxLength={12} value={agentPin}
            onChange={e => { setAgentPin(e.target.value); setAgentError(''); }}
            placeholder="PIN" aria-label="एजेंट का PIN / Agent PIN"
            style={{ width: '100%', padding: '14px', border: `2px solid ${agentError ? T.red.text : T.border}`, borderRadius: 8, fontSize: 22, textAlign: 'center', letterSpacing: 8, boxSizing: 'border-box', marginBottom: 12, background: T.bg2 }}
          />
          {agentError && <div role="alert" style={{ color: T.red.text, fontSize: 13, textAlign: 'center', marginBottom: 10 }}>{agentError}</div>}
          <button type="submit" disabled={!agentName.trim() || !agentPin || agentLoading}
            style={{ width: '100%', padding: '13px', background: T.ink, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: (agentName.trim() && agentPin && !agentLoading) ? 'pointer' : 'default', opacity: (agentName.trim() && agentPin && !agentLoading) ? 1 : 0.5 }}>
            {agentLoading ? 'जाँच हो रही है...' : 'अंदर जाएँ / Enter →'}
          </button>
        </form>
        <button type="button" onClick={() => { setShowAgentLogin(false); setAgentName(''); setAgentPin(''); setAgentError(''); }}
          style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 'none', color: T.mute, fontSize: 13, cursor: 'pointer' }}>
          ← वापस / Back
        </button>
      </div>
    );
  }

  // ── name + PIN (the whole point of decisions 2 + 4 — one screen, no post-login pick) ─────────
  return shell(
    <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: 340 }}>
      {options === null && !optionsError && (
        <div style={{ fontSize: 13, color: T.sec, textAlign: 'center', padding: '20px 0' }}>लोड हो रहा है... / Loading…</div>
      )}
      {optionsError && (
        <div role="alert" style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ color: T.red.text, fontSize: 13, marginBottom: 10 }}>{optionsError}</div>
          <button type="button" onClick={loadOptions} style={{ padding: '9px 18px', background: T.ink, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            फिर से कोशिश करें / Retry
          </button>
        </div>
      )}
      {options !== null && !optionsError && options.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: T.sec, lineHeight: 1.6 }}>
          अभी किसी का नाम सूची में नहीं है — मालिक से पूछें<br />
          <span style={{ color: T.mute, fontSize: 12 }}>Nobody is on this shop's roster yet — ask the owner</span>
        </div>
      )}
      {options !== null && !optionsError && options.length > 0 && (
        <form onSubmit={handleSubmit}>
          <label htmlFor="staff-name-select" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.mute, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            नाम / Name
          </label>
          <select id="staff-name-select" value={staffId}
            onChange={e => { setStaffId(e.target.value); setError(''); }}
            style={{ width: '100%', padding: '13px', border: `2px solid ${T.border}`, borderRadius: 8, fontSize: 15, marginBottom: 14, background: '#f8f9fa', boxSizing: 'border-box' }}>
            <option value="">— चुनें / Choose —</option>
            {options.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input
            type="password"
            inputMode="numeric"
            maxLength={12}
            value={pin}
            onChange={e => { setPin(e.target.value); setError(''); }}
            placeholder="● ● ● ●"
            aria-label="PIN"
            style={{
              width: '100%', padding: '14px', border: `2px solid ${error ? T.red.text : T.border}`,
              borderRadius: 8, fontSize: 22, textAlign: 'center', letterSpacing: 8,
              boxSizing: 'border-box', outline: 'none', marginBottom: 12, background: T.bg2,
            }}
          />
          {error && <div role="alert" style={{ color: T.red.text, fontSize: 13, textAlign: 'center', marginBottom: 10 }}>{error}</div>}
          <button type="submit" disabled={!staffId || !pin || loading}
            style={{
              width: '100%', padding: '13px', background: T.ink,
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
              cursor: (staffId && pin && !loading) ? 'pointer' : 'default',
              opacity: (staffId && pin && !loading) ? 1 : 0.5,
            }}>
            {loading ? 'जाँच हो रही है...' : 'अंदर जाएँ / Enter →'}
          </button>
        </form>
      )}
      {onForgotPin && (
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button onClick={onForgotPin}
            style={{ background: 'none', border: 'none', color: T.blue.text, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            PIN भूले? / Forgot PIN?
          </button>
        </div>
      )}
      {moduleWholesale && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button type="button" onClick={() => { setShowAgentLogin(true); setAgentError(''); }}
            style={{ background: 'none', border: 'none', color: T.mute, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            कलेक्शन एजेंट? अपना नाम लिखें / Collection agent? Enter your name →
          </button>
        </div>
      )}
    </div>
  );
}

function Login({ onLogin, demoMode, roles: rolesProp, shopName, onForgotPin, onAccountSignIn, staffLoginEnabled, onStaffLoginRequired, moduleWholesale }) {
  const roles = rolesProp || ROLES;

  // PH-47 / ADR-010 — once this shop has completed the forced staff-setup cutover, the login
  // screen is a name dropdown + PIN, never role cards (decisions 1, 2, 4). Everything below this
  // is the legacy shared-role-PIN screen, unchanged, still used pre-cutover and during the old
  // admin PIN's 7-day grace window.
  if (staffLoginEnabled) {
    return <StaffLogin onLogin={onLogin} shopName={shopName} onForgotPin={onForgotPin} onAccountSignIn={onAccountSignIn} moduleWholesale={moduleWholesale} />;
  }
  const [selectedRole, setSelectedRole] = useState(null);
  const [pin, setPin]                   = useState('');
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [warehouses, setWarehouses]     = useState(null);
  const [pendingRole, setPendingRole]   = useState(null);
  const [pendingPin, setPendingPin]     = useState(null);
  const [agentName, setAgentName]       = useState('');        // the agent types their own name (no roster)

  const selectRole = (id) => {
    setSelectedRole(id); setPin(''); setError(''); setAgentName('');
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (DEMO_MODE) {
      const role = roles[selectedRole];
      const stored = role.pin;
      const match = isPlainPin(stored) ? pin === stored : (await hashPin(pin)) === stored;
      if (match) { flushSync(() => { setPin(''); setSelectedRole(null); setError(''); }); onLogin(selectedRole, null, null); }
      else { setError('गलत PIN है — Wrong PIN'); setPin(''); }
      setLoading(false);
      return;
    }
    try {
      const isAgent = !!roles[selectedRole]?.dynamic;
      if (isAgent && !agentName.trim()) {
        setError('अपना नाम डालें / Enter your name'); setLoading(false); return;
      }
      const res = await loginPin(selectedRole, pin, null, isAgent ? agentName.trim() : null);
      if (res.data.needs_warehouse) {
        setWarehouses(res.data.warehouses);
        setPendingRole(selectedRole);
        setPendingPin(pin);
        setPin('');
        setLoading(false);
        return;
      }
      const r = isAgent ? 'agent' : selectedRole;
      const wid = res.data.warehouse_id;
      const wname = res.data.warehouse_name || res.data.agent_name || null;
      flushSync(() => { setPin(''); setSelectedRole(null); setError(''); });
      localStorage.setItem('session', JSON.stringify({ role: r, warehouse_id: wid, warehouse_name: wname, staff_id: res.data.staff_id ?? null, staff_name: res.data.staff_name ?? null, expiry: Date.now() + 8 * 60 * 60 * 1000 }));
      onLogin(r, wid, wname);
    } catch (err) {
      // PH-47 / ADR-010 — this shop cut over to per-person logins on another device; this one's
      // cached config is stale. Refresh it instead of showing a wrong/misleading "Wrong PIN" —
      // the PIN was fine, the screen is just out of date (adversarial + standard review finding).
      if (isStaffLoginRequired(err) && onStaffLoginRequired) {
        setError('यह दुकान अब व्यक्तिगत PIN से लॉगिन करती है — स्क्रीन अपडेट हो रही है... / This shop now uses personal logins — updating the screen…');
        setPin('');
        // Normally unmounts this screen entirely once tenantConfig updates; setLoading(false) in
        // .finally only matters if the refresh itself fails (offline), so the form doesn't stay
        // stuck disabled forever on a device that can't reach the backend right now either.
        Promise.resolve(onStaffLoginRequired()).finally(() => setLoading(false));
        return;
      }
      setError(errText(err, 'गलत PIN है — Wrong PIN'));
      setPin('');
      setLoading(false);
    }
  };

  const handleWarehouseSelect = async (warehouseId) => {
    try {
      const res = await loginPin(pendingRole, pendingPin, warehouseId);
      const r = pendingRole, wid = res.data.warehouse_id, wname = res.data.warehouse_name;
      flushSync(() => { setPendingPin(null); setPendingRole(null); setWarehouses(null); setPin(''); });
      localStorage.setItem('session', JSON.stringify({ role: r, warehouse_id: wid, warehouse_name: wname, staff_id: res.data.staff_id ?? null, staff_name: res.data.staff_name ?? null, expiry: Date.now() + 8 * 60 * 60 * 1000 }));
      onLogin(r, wid, wname);
    } catch (err) {
      if (isStaffLoginRequired(err) && onStaffLoginRequired) {
        setError('यह दुकान अब व्यक्तिगत PIN से लॉगिन करती है — स्क्रीन अपडेट हो रही है... / This shop now uses personal logins — updating the screen…');
        setWarehouses(null); setPin(''); setPendingPin(null); setPendingRole(null);
        onStaffLoginRequired();
        return;
      }
      setError(errText(err, 'Login failed. Please try again.'));
      setWarehouses(null);
      setPin('');
      setPendingPin(null);
      setPendingRole(null);
    }
  };

  // ── warehouse picker ────────────────────────────────────────────────────────
  if (warehouses) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>{shopName || 'हमारी दुकान'}</div>
          <div style={{ fontSize: 13, color: T.sec, marginTop: 4 }}>दुकान चुनें — Select your store</div>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 600 }}>
          {warehouses.map(w => (
            <div key={w.id} onClick={() => handleWarehouseSelect(w.id)}
              style={{ background: '#fff', border: `2px solid ${T.ink}`, borderRadius: 14, padding: '20px 32px', cursor: 'pointer', textAlign: 'center', minWidth: 160, boxShadow: '0 2px 8px rgba(30,58,95,0.12)', transition: 'all 0.15s' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🏪</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{w.warehouse_name}</div>
              <div style={{ fontSize: 12, color: T.mute, marginTop: 4 }}>{w.warehouse_code}</div>
            </div>
          ))}
        </div>
        {error && <div style={{ color: T.red.text, fontSize: 13, marginTop: 16 }}>{error}</div>}
        <button onClick={() => { setWarehouses(null); setPin(''); setError(''); setPendingPin(null); setPendingRole(null); }}
          style={{ marginTop: 20, background: 'none', border: 'none', color: T.mute, fontSize: 13, cursor: 'pointer' }}>
          ← वापस जाएँ / Back
        </button>
      </div>
    );
  }

  // ── main login ──────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>

      {/* Shop name */}
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>{shopName || 'हमारी दुकान'}</div>
        <div style={{ fontSize: 13, color: T.sec, marginTop: 4 }}>अपनी भूमिका चुनें — Select your role</div>
        {onAccountSignIn && (
          <button type="button" onClick={onAccountSignIn}
            style={{ background: 'none', border: 'none', color: T.blue.text, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', marginTop: 10 }}>
            मालिक? खाते से साइन इन करें / Owner? Sign in with your account →
          </button>
        )}
      </div>

      {/* Role cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
        {Object.values(roles).map(role => {
          const active = selectedRole === role.id;
          const roleLabel = `${role.label}. ${role.sublabel}. ${role.description}`;
          return (
            <button key={role.id} type="button" aria-label={roleLabel} aria-pressed={active} onClick={() => selectRole(role.id)}
              style={{
                background: active ? role.bg : '#fff',
                border: `2px solid ${active ? role.color : T.border}`,
                borderRadius: 14,
                padding: '20px 28px',
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: 155,
                transition: 'all 0.15s',
                boxShadow: active ? `0 4px 16px ${role.color}30` : '0 1px 4px rgba(0,0,0,0.08)',
                fontFamily: 'inherit',
                color: 'inherit',
                lineHeight: 'normal',
                appearance: 'none',
                WebkitAppearance: 'none',
                margin: 0,
              }}>
              <div style={{ fontSize: 38, marginBottom: 8 }}>{role.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: active ? role.color : T.ink2 }}>{role.label}</div>
              <div style={{ fontSize: 12, color: active ? role.color : T.mute, marginTop: 3 }}>{role.sublabel}</div>
              <div style={{ fontSize: 11, color: T.mute, marginTop: 6 }}>{role.description}</div>
            </button>
          );
        })}
      </div>

      {/* PIN entry */}
      {selectedRole && (
        <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: 340 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 24 }}>{roles[selectedRole].icon}</span>
            <div style={{ fontWeight: 700, fontSize: 16, color: T.ink, marginTop: 6 }}>
              {roles[selectedRole].label} — PIN डालें
            </div>
            <div style={{ fontSize: 12, color: T.mute, marginTop: 2 }}>Enter PIN to continue</div>
          </div>

          <form onSubmit={handlePinSubmit}>
            {roles[selectedRole].dynamic && (
              <input
                type="text"
                value={agentName}
                onChange={e => { setAgentName(e.target.value); setError(''); }}
                placeholder="अपना नाम डालें / Your name"
                autoComplete="off"
                autoCapitalize="words"
                maxLength={120}
                style={{ width: '100%', padding: '13px', border: '2px solid #ddd', borderRadius: 8, fontSize: 15, marginBottom: 12, background: '#f8f9fa', boxSizing: 'border-box' }}
              />
            )}
            <input
              type="password"
              inputMode="numeric"
              maxLength={12}
              value={pin}
              onChange={e => { setPin(e.target.value); setError(''); }}
              placeholder="● ● ● ●"
              style={{
                width: '100%', padding: '14px', border: `2px solid ${error ? T.red.text : T.border}`,
                borderRadius: 8, fontSize: 22, textAlign: 'center', letterSpacing: 8,
                boxSizing: 'border-box', outline: 'none', marginBottom: 12, background: T.bg2,
              }}
            />
            {error && <div style={{ color: T.red.text, fontSize: 13, textAlign: 'center', marginBottom: 10 }}>{error}</div>}
            <button type="submit" disabled={!pin || loading || (roles[selectedRole].dynamic && !agentName.trim())}
              style={{
                width: '100%', padding: '13px', background: roles[selectedRole].color,
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
                cursor: (pin && !loading) ? 'pointer' : 'default',
                opacity: (pin && !loading) ? 1 : 0.5,
              }}>
              {loading ? 'जाँच हो रही है...' : 'अंदर जाएँ / Enter →'}
            </button>
          </form>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <button onClick={() => selectRole(null)}
              style={{ background: 'none', border: 'none', color: T.mute, fontSize: 12, cursor: 'pointer' }}>
              ← वापस जाएँ / Back
            </button>
            {onForgotPin && (
              <button onClick={onForgotPin}
                style={{ background: 'none', border: 'none', color: T.blue.text, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                PIN भूले? / Forgot PIN?
              </button>
            )}
          </div>
        </div>
      )}

      {/* Demo PIN hint */}
      {demoMode && (
        <div style={{ marginTop: 28, background: T.yellow.bg, borderRadius: 10, padding: '12px 20px', fontSize: 12, color: T.sec, textAlign: 'center', maxWidth: 380, border: `1px solid ${T.yellow.border}` }}>
          <strong style={{ color: T.orange.text }}>Demo PINs:</strong>&nbsp;
          {Object.values(roles).filter(r => !r.dynamic).map((r, i) => (<span key={r.id}>{i > 0 && ' · '}{r.label}: <strong>{r.pin}</strong></span>))}
        </div>
      )}
    </div>
  );
}

export default Login;
